import { useMemo, useState } from 'react'

import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import styled from 'styled-components'

import { Button } from 'components/Button/Button'
import { addToast } from 'components/Toast/Toast'
import CreditCard from 'components/UI/Modal/CreditCard/CreditCard'
import { useModalContext } from 'context/modalContext'
import { PriceCard } from 'pages/settings/pages/subscriptionOptions/components/PriceCard'
import { getListCards, postCard, postDeposit } from 'services/endpoints/payment'
import { useSetUserProfile, useUserProfile } from 'store'
import { useAuthStore } from 'store'
import {
  type AddCardInfoRequestDto,
  type Card,
  type PrefillData
} from 'types/api/payment'

import FundsRadioGroup from './FundsRadioGroup/FundsRadioGroup'

type AddFundsProps = {
  defaultCard?: string | null
}

const AddFunds = ({ defaultCard }: AddFundsProps) => {
  const [funds, setFunds] = useState<number>(100)
  const [isAmountValid, setIsAmountValid] = useState<boolean>(true)

  const modalData = useModalContext()
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const userData = useUserProfile()

  /**
   * 1) Prefer cards from user profile
   * 2) Fallback to getListCards
   */
  const userCards = userData?.cards
  const hasUserCards = Array.isArray(userCards) && userCards.length > 0

  const { data: cardList } = useQuery(['allCards'], getListCards, {
    enabled: !hasUserCards
  })

  const cards = useMemo(() => {
    if (hasUserCards) return userCards
    return cardList?.cards ?? []
  }, [hasUserCards, userCards, cardList?.cards])

  const selectedCard = useMemo(() => {
    return cards?.find((c: any) => c.id === defaultCard)
  }, [cards, defaultCard])

  const hasValidCard = Boolean(selectedCard)

  // tax% comes from selected card country
  const taxRate = Number(selectedCard?.tax_rate ?? 0)

  const taxAmount = hasValidCard
    ? Number((funds * (taxRate / 100)).toFixed(2))
    : 0

  const totalAmount = Number((funds + taxAmount).toFixed(2))
const initialPrefillData: PrefillData = {
  id: '',
  name: '',
  cardNumber: '',
  expiration: '',
  cvc: '',
  country: '',
  address: '',
  zip: '',
  state: '',
  city: ''
}
  const updateFunds = (val: number) => {
    // PriceCard passes number (parseInt). Guard NaN / empty.
    const next = Number.isFinite(val) ? val : 0
    setFunds(next)

    // validate immediately (no blur needed)
    const ok = next >= 10 && next <= 200
    setIsAmountValid(ok)
  }

  const addDepositMutation = useMutation(
    async () => {
      if (!selectedCard?.id) {
        throw new Error('No card selected')
      }

    return await postDeposit({
      card_id: selectedCard.id,
      amount: funds
    })
    },
    {
      onSuccess: (res: any) => {
        const newWalletBalance = Number(res?.data?.amount ?? 0)

        useSetUserProfile({
          wallet_deposit: newWalletBalance
        })

        queryClient.invalidateQueries('user-profile')
        modalData.clearModal()
      },
      onError: (resp: any) => {
        toast.dismiss()

        const specific =
          resp?.response?.data?.message?.card_id?.[0] ||
          resp?.response?.data?.message ||
          resp?.response?.data?.error?.message ||
          'Failed to add funds'

        addToast('error', specific)
      }
    }
  )

const addCardMutation = useMutation(
  async (dto: AddCardInfoRequestDto) => {
    return await postCard(dto)
  },
  {
    onSuccess: response => {
      const newCard: Card | undefined = response?.data

      if (!newCard?.id) {
        return
      }

      const { userProfile, setUserProfile } = useAuthStore.getState()
      const currentCards: Card[] = userProfile?.cards ?? []

      const nextCards = currentCards
        .filter(card => card.id !== newCard.id)
        .map(card => ({
          ...card,
          is_default: newCard.is_default ? false : card.is_default
        }))

      nextCards.push(newCard)

      setUserProfile({
        cards: nextCards,
        ...(newCard.is_default
          ? { default_card: newCard.id }
          : {})
      })

      queryClient.invalidateQueries('allCards')
      queryClient.invalidateQueries('user-profile')
      modalData.clearModal()
    },
    onError: (resp: any) => {
      addToast(
        'error',
        resp?.response?.data?.error?.message ||
          resp?.response?.data?.message ||
          'Failed to add card'
      )
    }
  }
)
const openAddCardModal = () => {
  modalData.addModal(
    'Add Card',
    <CreditCard
      addNewFn={async (dto: AddCardInfoRequestDto) =>
        await addCardMutation.mutateAsync(dto)
      }
      prefillData={initialPrefillData}
    />
  )
}
  const checkIsAmountValid = (amount: number) => {
    const ok = amount >= 10 && amount <= 200
    setIsAmountValid(ok)
    return ok
  }

  function handleAddDeposit(): void {
    if (!hasValidCard) {
      addToast('error', 'Please Add A Card')
      return
    }
    if (isAmountValid) addDepositMutation.mutate()
  }

  return (
    <div>
      <Separator />

      <RelativeWrapper>
        {/* AddFunds-only overrides for PriceCard */}
        <AddFundsWrapper>
          <PriceCard
          
            label={t('settings:addFundsPriceCardLabel')}
            price={funds}
            updatePrice={updateFunds}
            validateFn={checkIsAmountValid}
            isValid={isAmountValid}
            customClass={`addfunds-pricecard ${!isAmountValid ? 'addfunds-pricecard--error' : ''}`}
          />

          {/* Error goes INSIDE the input area (right side) */}
          {!isAmountValid && (
            <InlineError>{t('You must enter valid amount!')}</InlineError>
          )}
        </AddFundsWrapper>
      </RelativeWrapper>

      {/* Under-input row: LEFT min/max, RIGHT fee/total */}
      <UnderInputRow>
        {hasValidCard ? (
          <MinMaxLeft>
            Min <strong>$10</strong> Max <strong>$200</strong>
          </MinMaxLeft>
        ) : (
          <NoCardError>You must add a card to deposit funds</NoCardError>
        )}

        {hasValidCard && (
          <TotalsRight>
            <TotalsRow>
              <TotalsLabel>{t('Tax')}:</TotalsLabel>
              <TotalsValue>${taxAmount.toFixed(2)}</TotalsValue>
            </TotalsRow>

            <TotalsRow>
              <TotalsLabel>{t('total')}:</TotalsLabel>
              <TotalsValue>${totalAmount.toFixed(2)}</TotalsValue>
            </TotalsRow>
          </TotalsRight>
        )}
      </UnderInputRow>

      <FundsRadioWrapper>
        <FundsRadioGroup selected={funds} onChange={(value: number) => setFunds(value)} />
      </FundsRadioWrapper>

      <FundsFooter>
        <Button
          text='Close'
          color='grey'
          font='mont-14-normal'
          width='10'
          height='3'
          clickFn={() => modalData.clearModal()}
        />
      {hasValidCard ? (
        <Button
          text='Buy Now'
          color='blue'
          font='mont-14-normal'
          width='13'
          height='4'
          clickFn={handleAddDeposit}
        />
      ) : (
        <Button
          text='Add Card'
          color='blue'
          font='mont-14-normal'
          width='13'
          height='4'
          clickFn={openAddCardModal}
        />
      )}
      </FundsFooter>
    </div>
  )
}

const RelativeWrapper = styled.div`
  position: relative;
`

const Separator = styled.div`
  border-top: 1px solid #d9d9d9;
  margin-bottom: 2.5rem;
`


const AddFundsWrapper = styled.div`
  position: relative;

  .addfunds-pricecard--error {
    border: 0.2rem solid #ff3b30 !important;
  }
`

/**
 * Error inside the input area, right side.
 * It sits in the reserved padding lane.
 */
const InlineError = styled.div`
  position: absolute;
  top: 50%;
  right: 2.2rem;
  transform: translateY(-50%);

  font-size: 1.3rem;
  font-weight: 500;
  color: #ff3b30;
  white-space: nowrap;
  pointer-events: none;
`

/**
 * Under-input row: min/max on LEFT, totals on RIGHT
 */
const UnderInputRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-top: 0.6rem;
  padding: 0 0.6rem;
`

const MinMaxLeft = styled.div`
  display: flex;
  align-items: center;
  font-size: 1.2rem;
  color: #5d5d5e;
  gap: 0.4rem;

  strong {
    font-weight: 600;
    color: #000;
  }
`

const TotalsRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
`

const TotalsRow = styled.div`
  display: flex;
  gap: 0.6rem;
  align-items: baseline;
`

const TotalsLabel = styled.span`
  font-size: 1.2rem;
  font-weight: 600;
  color: #5d5d5e;
`

const TotalsValue = styled.span`
  font-size: 1.2rem;
  font-weight: 600;
  color: #000;
`

const FundsRadioWrapper = styled.div`
  margin-top: 3rem;
`

const FundsFooter = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 2.5rem;
`
const NoCardError = styled.div`
  font-size: 1.2rem;
  font-weight: 500;
  color: #ff3b30;
`
export default AddFunds